import React, { useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { default as NotificationsIcon } from "@mui/icons-material/ChatBubbleOutline";
import {
  IconButton,
  Popover as MuiPopover,
  Tooltip,
  Badge,
} from "@mui/material";

import styled from "@emotion/styled";
import throttle from "lodash.throttle";

import AlertList from "components/notifications/AlertList";

import { useNotifications } from "hooks/useNotifications";

function NavbarNotificationsDropdown() {
  const {
    newNotificationCount,
    notifications,
    toggleNotificationSeen,
    showSeen,
    setShowSeen,
    query,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const { t } = useTranslation();

  const listRef = useRef<HTMLUListElement>(null);

  const checkAndFetchNextPage = useMemo(() => {
    return throttle(() => {
      const element = listRef.current;
      if (!element) return;

      const isScrollable = element.scrollHeight > element.clientHeight;
      const isAtBottom =
        element.scrollTop + element.offsetHeight >= element.scrollHeight - 50;

      if (
        (!isScrollable || isAtBottom) &&
        query.hasNextPage &&
        !query.isFetchingNextPage
      ) {
        query.fetchNextPage();
      }
    }, 100);
  }, [query]);

  useEffect(() => {
    const element = listRef.current;
    if (element) {
      element.addEventListener("scroll", checkAndFetchNextPage);
      checkAndFetchNextPage();
    }

    return () => {
      if (element) {
        element.removeEventListener("scroll", checkAndFetchNextPage);
      }
      checkAndFetchNextPage.cancel();
    };
  }, [checkAndFetchNextPage]);

  const visibleNotifications = useMemo(() => {
    if (!notifications) return [];
    if (showSeen) return notifications;
    return notifications.slice(0, 5);
  }, [notifications, showSeen]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setShowSeen(false);
  };

  return (
    <React.Fragment>
      <Tooltip title={t("navbar.notifications.tooltip")}>
        <IconButton color="inherit" ref={ref} onClick={handleOpen} size="large">
          <Badge badgeContent={newNotificationCount} color="secondary">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        sx={{
          maxHeight: "auto",
          ".MuiPaper-root": {
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <AlertList
          isLoading={query.isLoading}
          data={visibleNotifications}
          newAlertCount={newNotificationCount ?? 0}
          toggleAlertSeen={toggleNotificationSeen}
          isExpanded={showSeen}
          setIsExpanded={setShowSeen}
          listRef={listRef}
          isFetching={query.isFetching || query.isFetchingNextPage}
        />
      </Popover>
    </React.Fragment>
  );
}

export default NavbarNotificationsDropdown;

const Popover = styled(MuiPopover)`
  .MuiPaper-root {
    width: 300px;
    ${(props) => props.theme.shadows[1]};
    border: 1px solid ${(props) => props.theme.palette.divider};
  }
`;
